import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { prefersReducedMotion } from "../lib/reducedMotion";

gsap.registerPlugin(ScrollTrigger);

type ScrollState = {
  progress: number;
  sectionIndex: number;
};

const ScrollContext = createContext<ScrollState>({ progress: 0, sectionIndex: 0 });

export function ScrollProvider({ children, sectionCount }: { children: ReactNode; sectionCount: number }) {
  const [state, setState] = useState<ScrollState>({ progress: 0, sectionIndex: 0 });

  useEffect(() => {
    const reduced = prefersReducedMotion();
    const lenis = new Lenis({
      lerp: reduced ? 1 : 0.075,
      smoothWheel: !reduced,
      anchors: true
    });

    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    const update = () => {
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = documentHeight <= 0 ? 0 : Math.min(1, Math.max(0, window.scrollY / documentHeight));
      const sectionIndex = Math.min(sectionCount - 1, Math.max(0, Math.floor(progress * sectionCount)));
      setState({ progress, sectionIndex });
    };

    const trigger = ScrollTrigger.create({
      start: 0,
      end: "max",
      onUpdate: update
    });

    update();
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
      trigger.kill();
      gsap.ticker.remove(tick);
      lenis.destroy();
    };
  }, [sectionCount]);

  const value = useMemo(() => state, [state]);
  return <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>;
}

export function useScrollState() {
  return useContext(ScrollContext);
}
